import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthProvider";
import { useModal } from "../../context/ModalProvider";
import { useAccountLoginUrl } from "../../hooks/useAccountLoginUrl";
import {
  clearReferralCache,
  loadReferral,
  type ReferralInfo,
} from "../../lib/referralApi";
import { LucideIcon } from "./LucideIcon";
import { InviteFriendModal, type InviteVariant } from "./InviteFriendModal";
import "./referralBand.css";

/**
 * Public (respondent) URL of the feedback Google Form — the admin link ends in
 * `/edit`; visitors need `/viewform`.
 */
const FEEDBACK_FORM_URL =
  "https://docs.google.com/forms/d/1jHQbXfce80doNmvyY28lPJ7CYDFoPH6CV1wLkAgJNdA/viewform";

/**
 * Refer-a-friend subfooter band — rendered by `<Page app>` after `<main>` on
 * every in-app page, above the Shopify theme footer. Deep-plum gradient band
 * per the refer-a-friend prototype.
 *
 * Three variants:
 *   - customer with a Yotpo referral link → full "$10 off each" pitch + code line
 *   - customer whose link hasn't resolved (not enrolled yet / Yotpo down /
 *     keys unset) → same band, softer pitch, generic share link
 *   - anon visitor → generic share link + a log-in nudge to earn rewards
 *
 * The referral fetch is customer-only and session-cached in `referralApi` —
 * anon responses are never cached, so an anon→customer flip can't serve a
 * stale generic result. The band renders immediately; only the code line
 * waits for the fetch (no layout shift).
 */
export function ReferralBand() {
  const auth = useAuth();
  const modal = useModal();
  const kind = auth.state.kind;
  const loginUrl = useAccountLoginUrl();

  const [referral, setReferral] = useState<ReferralInfo | null>(null);

  // Belt-and-braces for a mid-mount auth flip (bootstrap resolving anon →
  // customer without a route change): drop the session cache so the customer
  // fetch below can't join a promise started under the old principal. Across
  // route changes the band remounts and `prevKind` re-seeds, so this never
  // clears the cache on ordinary navigation.
  const prevKind = useRef(kind);
  useEffect(() => {
    if (prevKind.current !== kind) {
      prevKind.current = kind;
      clearReferralCache();
      setReferral(null);
    }
  }, [kind]);

  useEffect(() => {
    if (kind !== "customer") return;
    let cancelled = false;
    loadReferral()
      .then((res) => {
        if (!cancelled) setReferral(res.referral);
      })
      .catch(() => {
        // Band stays on the generic variant; `loadReferral` drops the failed
        // promise so a later mount retries.
      });
    return () => {
      cancelled = true;
    };
  }, [kind]);

  // Render only for resolved principals — during bootstrap (`loading`) and on
  // gate/error states the band would flash the wrong copy.
  if (kind !== "customer" && kind !== "anon") return null;

  const variant: InviteVariant =
    kind === "anon" ? "anon" : referral ? "reward" : "pending";
  const shareUrl = referral?.link ?? window.location.origin;

  const openInvite = () => {
    const id = modal.open(
      <InviteFriendModal
        variant={variant}
        shareUrl={shareUrl}
        code={referral?.code ?? null}
        onClose={() => modal.close(id)}
      />,
    );
  };

  return (
    <aside className="fgrf-band" aria-label="Refer a friend">
      <div className="fgrf-band__glow fgrf-band__glow--purple" aria-hidden />
      <div className="fgrf-band__glow fgrf-band__glow--lime" aria-hidden />
      {/* Background is full-bleed; content sits in the shared page column so it
          lines up with the sub-nav and screen bodies. */}
      <div className="fg-page-width">
        <div className="fgrf-band__inner">
          <div className="fgrf-band__left">
            <div className="fgrf-band__icon" aria-hidden>
              <LucideIcon
                name="gift"
                size={26}
                color="var(--fg-lime)"
                strokeWidth={1.9}
              />
            </div>
            <div>
              <div className="fgrf-band__eyebrow">Refer a friend</div>
              <h2 className="fgrf-band__title">
                Good fuel is better with company.
              </h2>
              <p className="fgrf-band__lede">
                {variant === "reward" && (
                  <>
                    Getting the most out of your training? Pass Fuel Goods along
                    to a running friend who&rsquo;d get just as much out of it —
                    and you&rsquo;ll <strong>each get $10 off</strong> when they
                    place their first order.
                  </>
                )}
                {variant === "pending" && (
                  <>
                    Getting the most out of your training? Pass Fuel Goods along
                    to a running friend who&rsquo;d get just as much out of it.
                    Your referral code isn&rsquo;t ready yet — share Fuel Goods
                    below and check back soon for rewards.
                  </>
                )}
                {variant === "anon" && (
                  <>
                    Getting the most out of your training? Pass Fuel Goods along
                    to a running friend who&rsquo;d get just as much out of it —
                    and log in to join Team Fuel Goods to{" "}
                    <strong>earn referral rewards</strong> when they order.
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="fgrf-band__cta">
            <button
              type="button"
              className="fgrf-band__btn"
              onClick={openInvite}
            >
              <LucideIcon name="share-2" size={18} strokeWidth={2.2} />
              Invite a friend
            </button>
            {variant === "reward" && referral?.code && (
              <span className="fgrf-band__codeline">
                Your code ·{" "}
                <span className="fgrf-band__code">{referral.code}</span>
              </span>
            )}
            {variant === "anon" && (
              <a className="fgrf-band__login" href={loginUrl}>
                Log in to earn referral rewards
              </a>
            )}
          </div>
        </div>
        <p className="fgrf-band__feedback">
          This app is new and we&rsquo;re constantly improving it. Have a
          suggestion? Found a bug?{" "}
          <a href={FEEDBACK_FORM_URL} target="_blank" rel="noopener noreferrer">
            We&rsquo;d love your feedback
          </a>
          .
        </p>
      </div>
    </aside>
  );
}
